import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { tableRows } from "../../utils/xlsx";
import { AsOfFilter, cellRight, download, EmptyState, ExportButton, LoadingOverlay, money, REPORTS, sheetHeading, StatCard, todayIso } from "./common";

const COLUMNS = [
  { label: "SKU", key: "skuCode" },
  { label: "Product", key: "productName" },
  { label: "Category", key: "category" },
  { label: "Quantity", key: "quantity", format: "money", total: false },
  { label: "Rate", key: "rate", format: "money", total: false },
  { label: "Value", key: "value", format: "money" },
  { label: "Rate Basis", key: "rateBasis" },
];

/** Stock on a date valued at weighted-average purchase cost — the opening / closing stock figure in the P&L and Balance Sheet. */
const StockValuation = () => {
  const [asOfDate, setAsOfDate] = useState(todayIso);
  // the price stock is valued at: purchase price (cost) or selling price
  const [priceBasis, setPriceBasis] = useState("PURCHASE");
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!asOfDate) {
      toast.error("Select a date");
      return;
    }
    setLoading(true);
    try {
      setReport(await api.get(`${REPORTS}/stock-valuation`, { asOfDate, priceBasis }));
    } catch (error) {
      toast.error(error.message || "Failed to load stock valuation");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceBasis]);

  const categories = useMemo(() => [...new Set((report?.items || []).map((i) => i.category || "Uncategorized"))].sort(), [report]);
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (report?.items || []).filter(
      (item) =>
        (!category || (item.category || "Uncategorized") === category) &&
        (!needle || (item.productName || "").toLowerCase().includes(needle) || (item.skuCode || "").toLowerCase().includes(needle))
    );
  }, [report, category, search]);
  const filteredValue = rows.reduce((sum, row) => sum + Number(row.value || 0), 0);
  const unvalued = (report?.items || []).filter((item) => !Number(item.rate)).length;

  const exportExcel = () =>
    download(`Stock Valuation ${report.asOfDate}`, [
      {
        name: "Stock Valuation",
        rows: [...sheetHeading("Stock Valuation", `As on ${report.asOfDate} — ${report.method}`), ...tableRows(COLUMNS, rows, { totals: true })],
        widths: [16, 36, 20, 12, 14, 16, 36],
      },
    ]);

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} />
      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        {priceBasis === "SELLING"
          ? "Quantity on hand × the product's selling price in its price list."
          : "Quantity on hand × average purchase cost (excluding GST; the price list's purchase price when there is no purchase bill). This is the closing stock used in Profit & Loss, Balance Sheet and inventory turnover."}
      </div>
      <div className="filters-bar">
        <AsOfFilter value={asOfDate} onChange={setAsOfDate} />
        <select value={priceBasis} onChange={(e) => setPriceBasis(e.target.value)} aria-label="Value at">
          <option value="PURCHASE">Purchase Price</option>
          <option value="SELLING">Selling Price</option>
        </select>
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Run Report"}
        </button>
        <ExportButton onClick={exportExcel} disabled={!report} />
      </div>

      {!report ? (
        <EmptyState>{loading ? "Loading..." : "No data loaded"}</EmptyState>
      ) : (
        <>
          <div className="stat-grid" style={{ marginTop: "16px" }}>
            <StatCard label="Total Stock Value" value={`₹${money(report.totalValue)}`} hint={report.method} />
            <StatCard label="Products in Stock" value={report.items.length} />
            <StatCard
              label="Products Without a Cost"
              value={unvalued}
              hint={priceBasis === "SELLING" ? "No selling price — valued at zero" : "No purchase bill or purchase price — valued at zero"}
              tone={unvalued > 0 ? "bad" : undefined}
            />
          </div>
          <div className="filters-bar">
            <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category">
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input type="text" placeholder="Search product or SKU" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  {COLUMNS.map((column) => (
                    <th key={column.key} style={column.format ? cellRight : { textAlign: "left" }}>
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length}>
                      <EmptyState>No stock on this date</EmptyState>
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.productId}>
                      <td style={{ textAlign: "left" }}>{row.skuCode}</td>
                      <td style={{ textAlign: "left" }}>{row.productName}</td>
                      <td style={{ textAlign: "left" }}>{row.category || "-"}</td>
                      <td style={cellRight}>{Number(row.quantity).toLocaleString("en-IN", { maximumFractionDigits: 3 })}</td>
                      <td style={cellRight}>{money(row.rate)}</td>
                      <td style={cellRight}>{money(row.value)}</td>
                      <td style={{ textAlign: "left", color: "var(--color-text-muted)", fontSize: "12.5px" }}>{row.rateBasis}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {rows.length ? (
                <tfoot>
                  <tr>
                    <td colSpan={5} style={{ textAlign: "right", fontWeight: 700 }}>
                      Total
                    </td>
                    <td style={{ ...cellRight, fontWeight: 700 }}>{money(filteredValue)}</td>
                    <td />
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default StockValuation;
